import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AttributeDataType, AttributeDefInline, AttributeDefView, AttributeView, EnumOption } from "../api/catalog";
import { dataTypeLabel } from "./AttributeFormModal";
import { Select, type SelectOption } from "./Select";
import Ic from "./icons.jsx";

/**
 * One attribute picked for the type. Local state only — turned into
 * `AttributeDefInline[]` by {@link inlineDefsFromDrafts} right before the type
 * is saved (sprint 5f: the picks travel with the type request). What the
 * attribute is (label, key, data type, unit, list values) belongs to the
 * codebook and is carried here only for display; the type owns `required` and
 * the order.
 */
export type AttributeDefDraft = {
  /** Local React key; never sent. */
  rowId: string;
  attributeId: string;
  key: string;
  label: string;
  dataType: AttributeDataType;
  unit: string | null;
  enumOptions: EnumOption[] | null;
  required: boolean;
};

let rowSeq = 0;
function nextRowId(): string {
  rowSeq += 1;
  return `row-${rowSeq}`;
}

/** A codebook attribute freshly picked for the type; not mandatory until ticked. */
export function draftFromAttribute(a: AttributeView): AttributeDefDraft {
  return {
    rowId: nextRowId(),
    attributeId: a.id,
    key: a.key,
    label: a.label,
    dataType: a.dataType,
    unit: a.unit,
    enumOptions: a.enumOptions,
    required: false,
  };
}

/** Server definitions → drafts, in display order (sortOrder, then key). */
export function draftsFromDefs(defs: AttributeDefView[]): AttributeDefDraft[] {
  return [...defs]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))
    .map((d) => ({
      rowId: nextRowId(),
      attributeId: d.attributeId,
      key: d.key,
      label: d.label,
      dataType: d.dataType,
      unit: d.unit,
      enumOptions: d.enumOptions,
      required: d.required,
    }));
}

/** What the type request carries: `sortOrder` = 10 × row index, so the table order is the saved order. */
export function inlineDefsFromDrafts(drafts: AttributeDefDraft[]): AttributeDefInline[] {
  return drafts.map((d, i) => ({ attributeId: d.attributeId, required: d.required, sortOrder: i * 10 }));
}

/** "Snaga (kW)" — how an attribute is offered in the picker. */
function pickerLabel(a: AttributeView): string {
  return a.unit ? `${a.label} (${a.unit})` : a.label;
}

/**
 * The attributes a part type records (sprint 5f): picked from the attribute
 * codebook, never typed in here. A row shows what the codebook says about the
 * attribute (read-only) and lets the type decide whether a value is mandatory;
 * rows can be reordered and removed. `attributes` is the codebook — the picker
 * offers the ones the type does not have yet.
 */
export function AttributeDefsEditor({
  drafts, onChange, attributes, disabled = false, errors = {}, footerExtra,
}: {
  drafts: AttributeDefDraft[];
  onChange: (next: AttributeDefDraft[]) => void;
  /** The attribute codebook; an empty list while it loads. */
  attributes: AttributeView[];
  disabled?: boolean;
  /** rowId → message from the server (`details.attributeDefs[i]…`). */
  errors?: Record<string, string>;
  /** Extra controls next to the picker (the page's "Novi atribut" button). */
  footerExtra?: ReactNode;
}) {
  const { t } = useTranslation();

  const byId = useMemo(() => new Map(attributes.map((a) => [a.id, a])), [attributes]);
  const pickOptions = useMemo<SelectOption[]>(() => {
    const taken = new Set(drafts.map((d) => d.attributeId));
    return attributes
      .filter((a) => !taken.has(a.id))
      .map((a) => ({ value: a.id, label: pickerLabel(a), keywords: `${a.key} ${dataTypeLabel(t, a.dataType)}` }));
  }, [attributes, drafts, t]);

  function patch(rowId: string, change: Partial<AttributeDefDraft>) {
    onChange(drafts.map((d) => (d.rowId === rowId ? { ...d, ...change } : d)));
  }

  function add(attributeId: string) {
    const a = byId.get(attributeId);
    if (a) onChange([...drafts, draftFromAttribute(a)]);
  }

  function move(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= drafts.length) return;
    const next = [...drafts];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  function remove(rowId: string) {
    onChange(drafts.filter((d) => d.rowId !== rowId));
  }

  const picker = !disabled && (
    <div className="attr-editor-foot">
      {pickOptions.length > 0 ? (
        <Select
          value=""
          options={pickOptions}
          onChange={add}
          placeholder={t("attributeDefs.pick", "Dodaj atribut iz šifarnika…")}
          ariaLabel={t("attributeDefs.pick", "Dodaj atribut iz šifarnika…")}
          style={{ flex: "1 1 260px", maxWidth: 360 }}
        />
      ) : (
        <span className="muted" style={{ fontSize: 13 }}>
          {attributes.length === 0
            ? t("attributeDefs.codebookEmpty", "Šifarnik atributa je prazan.")
            : t("attributeDefs.allPicked", "Svi atributi iz šifarnika su već dodati.")}
        </span>
      )}
      {footerExtra}
    </div>
  );

  if (drafts.length === 0) {
    return (
      <div>
        <div className="empty">
          {t("attributeDefs.empty", "Izaberi iz šifarnika atribute koje ovaj tip vodi (npr. Snaga, Obrtaji, Kućište). Vrednosti se unose na poziciji (referentne) i na komadu (stvarne).")}
        </div>
        {picker}
      </div>
    );
  }

  const colKey = t("attributeDefs.col.key", "Ključ");
  const colLabel = t("attributeDefs.col.label", "Naziv");
  const colType = t("attributeDefs.col.dataType", "Tip podatka");
  const colUnit = t("attributeDefs.col.unit", "Jedinica");
  const colEnum = t("attributeDefs.col.enumValues", "Vrednosti liste");
  const colRequired = t("attributeDefs.col.required", "Obavezno");
  const dash = <span className="muted">—</span>;

  return (
    <div>
      <div className="attr-editor-wrap">
        <table className="tbl attr-editor" aria-label={t("attributeDefs.tableLabel", "Atributi tipa")}>
          <thead>
            <tr>
              <th>{colLabel}</th>
              <th>{colKey}</th>
              <th>{colType}</th>
              <th>{colUnit}</th>
              <th>{colEnum}</th>
              <th>{colRequired}</th>
              {!disabled && <th aria-label={t("common.actions", "Akcije")} />}
            </tr>
          </thead>
          <tbody>
            {drafts.map((d, i) => {
              // The codebook is the truth; the draft's copy covers the moment before it loads.
              const a = byId.get(d.attributeId) ?? d;
              const rowError = errors[d.rowId];
              return (
                <tr key={d.rowId} className={rowError ? "has-error" : undefined}>
                  <td data-label={colLabel}>
                    <b>{a.label}</b>
                    {rowError && <div className="attr-editor-error" role="alert">{rowError}</div>}
                  </td>
                  <td className="mono" data-label={colKey}>{a.key}</td>
                  <td data-label={colType}>{dataTypeLabel(t, a.dataType)}</td>
                  <td data-label={colUnit}>{a.unit || dash}</td>
                  <td className="cell-list" data-label={colEnum}>
                    {a.dataType === "ENUM" && a.enumOptions?.length ? a.enumOptions.map((o) => o.value).join(", ") : dash}
                  </td>
                  <td data-label={colRequired}>
                    <input
                      type="checkbox"
                      checked={d.required}
                      onChange={(e) => patch(d.rowId, { required: e.target.checked })}
                      disabled={disabled}
                      aria-label={`${colRequired}: ${a.label}`}
                    />
                  </td>
                  {!disabled && (
                    <td className="card-actions attr-editor-actions">
                      <span className="tip-wrap" data-tip={t("attributeDefs.moveUpShort", "Gore")}>
                        <button type="button" className="btn sm ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("attributeDefs.moveUp", "Pomeri gore: {{name}}", { name: a.label })}>
                          <Ic.arrowUp />
                        </button>
                      </span>
                      <span className="tip-wrap" data-tip={t("attributeDefs.moveDownShort", "Dole")}>
                        <button type="button" className="btn sm ghost" disabled={i === drafts.length - 1} onClick={() => move(i, 1)} aria-label={t("attributeDefs.moveDown", "Pomeri dole: {{name}}", { name: a.label })}>
                          <Ic.arrowDown />
                        </button>
                      </span>
                      <button type="button" className="link-btn danger" onClick={() => remove(d.rowId)} aria-label={t("attributeDefs.remove", "Ukloni atribut sa tipa: {{name}}", { name: a.label })}>
                        {t("action.remove", "Ukloni")}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {picker}
      {!disabled && (
        <div className="attr-editor-hint muted">
          {t("attributeDefs.orderHint", "Redosled u tabeli je redosled prikaza na assetu i poziciji.")}
        </div>
      )}
    </div>
  );
}
